import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { I18NService } from '@core';
import { SocialService } from '@delon/auth';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzAffixModule } from 'ng-zorro-antd/affix';
import { NzBreadCrumbModule } from 'ng-zorro-antd/breadcrumb';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzI18nModule } from 'ng-zorro-antd/i18n';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

@Component({
  selector: 'app-account-list',
  providers: [SocialService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NzCardModule,
    NzFormModule,
    ReactiveFormsModule,
    NzFormModule,
    NzButtonModule,
    NzTableModule,
    NzI18nModule,
    NzSpinModule,
    NzIconModule,
    NzInputModule,
    NzBreadCrumbModule,
    NzPaginationModule,
    FormsModule,
    NzAffixModule,
    NzToolTipModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './account-list.component.html',
  styleUrls: ['./account-list.component.less']
})
export class AccountListComponent implements OnInit {
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  loading = false;
  pageSize = 20;
  pageIndex = 1;
  total = 0;
  listOfData: any[] = [];
  searchKeywords: any;
  refreshTime = Date.now();
  direction = 'desc';

  columns = [
    { title: this.i18n.fanyi('account.list.account-id'), key: 'id' },
    { title: this.i18n.fanyi('account.list.country-code'), key: 'countryCode' },
    { title: this.i18n.fanyi('account.list.phone-number'), key: 'mobile' },
    { title: this.i18n.fanyi('account.list.create-time'), key: 'createTime' },
    { title: this.i18n.fanyi('account.list.account-source'), key: 'channel' }
  ];

  constructor(
    private readonly fb: FormBuilder,
    private readonly http: HttpService,
    private readonly message: NzMessageService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  loadData(reset: boolean = false): void {
    if (reset) {
      this.pageIndex = 1;
    }

    this.loading = true;
    const params = {
      keyword: this.searchKeywords,
      pageSize: this.pageSize,
      pageIndex: this.pageIndex - 1,
      direction: this.direction,
      refreshTime: this.refreshTime
    };

    this.http
      .post(ConstantUtil.ADMIN_ACCOUNT_LIST, params)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res: any) => {
          if (res.success) {
            this.listOfData = res.data.items;
            this.total = res.data.totalCount;
            this.cdr.detectChanges();
          } else {
            this.message.error(res.msg ?? this.i18n.fanyi('common.load-failed'));
          }
        },
        error: () => this.message.error(this.i18n.fanyi('common.load-failed'))
      });
  }
  search(): void {
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData();
  }

  reset(): void {
    this.searchKeywords = '';
    this.refreshTime = Date.now();
    this.pageIndex = 1;
    this.loadData();
  }

  nzPageIndexChange(index: number): void {
    this.pageIndex = index;
    this.loadData();
  }

  nzPageSizeChange(size: number): void {
    this.pageSize = size;
    this.refreshTime = Date.now();
    this.pageIndex = 1;
    this.loadData();
  }

  viewAccount(accountId: string): void {
    if (accountId) {
      this.router.navigate(['/account/detail', accountId]);
    } else {
      this.message.error(this.i18n.fanyi('account.list.account-id-required'));
    }
  }
}
